import { useMemo, useRef, useState } from 'react';
import { Check, Copy, Download, Loader2, Upload, UploadCloud } from 'lucide-react';
import { uploadMedia } from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { CardEditor, type CardEditorHandle, type CardValue } from './CardEditor';
import { exportExt, type ExportFormat } from './photoSlot';
import { FormatToggle } from './FormatToggle';
import {
  CARD_TEMPLATES,
  platformsOf,
  resolveTemplate,
  type CardTemplate,
  type Platform,
  type Variant,
} from './templates';

// Generated cards land in their own storage folder (see DAL storageCategoryFolder).
const CARD_MEDIA_CATEGORY = 'social_card' as const;

const slugify = (s: string) =>
  (s || 'artist').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export interface CardComposerProps {
  /** Templates the user may pick from. Defaults to all. */
  templates?: CardTemplate[];
  /** Initial artist name (e.g. prefilled from a profile). */
  initialName?: string;
  /** Initial show/programme name (resident layouts). */
  initialShowName?: string;
  /** Hide the name input and keep `initialName` fixed (locked-down flows). */
  lockName?: boolean;
  /** Preselect a platform (Instagram / SoundCloud). */
  initialPlatform?: Platform;
  /** Preselect a layout variant — e.g. 'resident' for a residency show. */
  initialVariant?: Variant;
  /** Passed to uploadMedia for multi-tenant path prefixing. */
  accountSlug?: string;
  /** Anonymous-upload auth for public/invite flows (no admin JWT). */
  uploadAuth?: { token: string; entityId: string };
  /**
   * Called after a successful R2 upload with the public URL + the template id,
   * so the caller can attach the card to a record (e.g. a show). If it throws,
   * the error is surfaced and the "saved" state is not shown.
   */
  onUploaded?: (url: string, templateId: string) => Promise<void> | void;
  /** Label for the save button (e.g. "Save to show"). */
  saveLabel?: string;
  /** Disable the save button (e.g. no show selected yet) with an explanatory hint. */
  saveDisabled?: boolean;
  saveDisabledHint?: string;
  className?: string;
}

/**
 * Self-contained social-card composer: photo upload + reposition + name, with
 * Download and Save-to-storage. Used by the admin page and (behind a flag) the
 * artist invite flow — the only difference is which templates are offered and
 * whether the name is editable.
 */
export function CardComposer({
  templates = CARD_TEMPLATES,
  initialName = '',
  initialShowName = '',
  lockName = false,
  initialPlatform,
  initialVariant,
  accountSlug,
  uploadAuth,
  onUploaded,
  saveLabel = 'Save to storage',
  saveDisabled = false,
  saveDisabledHint,
  className,
}: CardComposerProps) {
  const available = templates.length > 0 ? templates : CARD_TEMPLATES;
  const platforms = useMemo(() => platformsOf(available), [available]);

  const [platform, setPlatform] = useState<Platform>(initialPlatform ?? platforms[0].platform);
  const [variant, setVariant] = useState<Variant>(initialVariant ?? 'guest');
  const template = useMemo(
    () => resolveTemplate(available, platform, variant) ?? available[0],
    [available, platform, variant],
  );

  // Does the current platform offer a resident layout to toggle to?
  const hasResident = useMemo(
    () => available.some((t) => t.platform === platform && t.variant === 'resident'),
    [available, platform],
  );

  const [value, setValue] = useState<CardValue>({
    artistName: initialName,
    showName: initialShowName,
    photoSrc: null,
    transform: null,
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('jpeg');

  const editorRef = useRef<CardEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Any edit invalidates a previously-saved URL.
  const clearSaved = () => {
    setUploadedUrl(null);
    setUploadError(null);
    setCopied(false);
  };

  const updateValue = (next: CardValue | ((v: CardValue) => CardValue)) => {
    clearSaved();
    setValue(next as CardValue);
  };

  const changePlatform = (p: Platform) => {
    clearSaved();
    setPlatform(p);
    // A different platform means a different frame size — re-fit the photo.
    setValue((v) => ({ ...v, transform: null }));
  };

  const changeVariant = (vr: Variant) => {
    clearSaved();
    setVariant(vr);
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    clearSaved();
    // New photo resets placement so the editor cover-fits it.
    setValue((v) => ({ ...v, photoSrc: url, transform: null }));
  };

  const handleDownload = () => {
    const url = editorRef.current?.toDataURL(format);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(value.artistName)}-${template.id}.${exportExt(format)}`;
    a.click();
  };

  const handleUpload = async () => {
    const blob = await editorRef.current?.toBlob(format);
    if (!blob) {
      setUploadError('Could not render the card. Try again.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const base = `${slugify(value.artistName)}-${template.id}`;
      const result = await uploadMedia(blob, {
        category: CARD_MEDIA_CATEGORY,
        identifier: base,
        fileName: `${base}.${exportExt(format)}`,
        accountSlug,
        auth: uploadAuth,
      });
      // Persist the reference (e.g. attach to a show) before showing success.
      await onUploaded?.(result.url, template.id);
      setUploadedUrl(result.url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = async () => {
    if (!uploadedUrl) return;
    try {
      await navigator.clipboard.writeText(uploadedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the URL is still shown for manual copy */
    }
  };

  return (
    <div className={className}>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Canvas */}
        <div className="flex justify-center">
          <div className="w-full max-w-[520px]">
            <CardEditor ref={editorRef} template={template} value={value} onChange={updateValue} />
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-5">
          {platforms.length > 1 && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Platform</label>
              <select
                value={platform}
                onChange={(e) => changePlatform(e.target.value as Platform)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {platforms.map((p) => (
                  <option key={p.platform} value={p.platform}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {hasResident && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Layout</label>
              <div className="inline-flex rounded-md border border-input p-0.5">
                {(['guest', 'resident'] as Variant[]).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => changeVariant(v)}
                    className={`rounded px-3 py-1 text-sm capitalize transition-colors ${
                      variant === v
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              {variant === 'resident' && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Resident layout adds a show-name banner at the top.
                </p>
              )}
            </div>
          )}

          {template.showName && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Show name</label>
              <input
                type="text"
                value={value.showName ?? ''}
                onChange={(e) => updateValue((v) => ({ ...v, showName: e.target.value }))}
                placeholder="e.g. Rhythmical Rotation"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          )}

          {!lockName && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Artist name</label>
              <input
                type="text"
                value={value.artistName}
                onChange={(e) => updateValue((v) => ({ ...v, artistName: e.target.value }))}
                placeholder="e.g. Bubble Bouillon"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-medium">Photo</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              {value.photoSrc ? 'Replace photo' : 'Upload photo'}
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Drag on the image to reposition; use the zoom slider under the canvas.
            </p>
          </div>

          <div className="space-y-2 border-t pt-5">
            <FormatToggle value={format} onChange={setFormat} />
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleDownload}
              disabled={!value.photoSrc}
            >
              <Download className="mr-2 h-4 w-4" />
              Download {format === 'jpeg' ? 'JPG' : 'PNG'}
            </Button>

            <Button
              type="button"
              className="w-full"
              onClick={handleUpload}
              disabled={!value.photoSrc || uploading || saveDisabled}
            >
              {uploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UploadCloud className="mr-2 h-4 w-4" />
              )}
              {uploading ? 'Saving…' : saveLabel}
            </Button>

            {saveDisabled && saveDisabledHint && (
              <p className="text-xs text-muted-foreground">{saveDisabledHint}</p>
            )}

            {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}

            {uploadedUrl && (
              <div className="rounded-md border border-input bg-muted/40 p-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">{saveLabel} — done</p>
                <div className="flex items-center gap-2">
                  <a
                    href={uploadedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 truncate text-xs text-primary hover:underline"
                    title={uploadedUrl}
                  >
                    {uploadedUrl}
                  </a>
                  <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
